import { TranslateService } from '@ngx-translate/core';
import { User } from '@frontend-site/shared/data';

/** Users that are no longer selectable but may still be referenced by a stored value. */
export interface UnconnectedUsers {
    disconnectedUsers: User[] | null;
    inactiveUsers: User[] | null;
}

/** What a user field renders for its current value: the text plus an optional prefix icon with its tooltip. */
export interface UserDisplay {
    text: string;
    icon: string | null;
    tooltip: string;
}

export function textOnlyDisplay(text: string): UserDisplay {
    return { text, icon: null, tooltip: '' };
}

/**
 * Resolves the display of a user that is not in the selectable list: a disconnected user wins over an inactive one.
 * Pure — safe to call from template bindings. Returns `null` when the id matches neither.
 */
export function resolveUnconnectedUserDisplay(
    userId: number | string,
    unconnectedUsers: UnconnectedUsers,
    translate: TranslateService
): UserDisplay | null {
    const disconnectedUser = (unconnectedUsers.disconnectedUsers ?? []).find((u) => u.id === userId);
    if (disconnectedUser) {
        return {
            text: fullName(disconnectedUser),
            icon: 'slash',
            tooltip: translate.instant('UserManagement.UserAvatar.DisconnectedUser')
        };
    }
    const inactiveUser = (unconnectedUsers.inactiveUsers ?? []).find((u) => u.id === userId);
    if (inactiveUser) {
        return {
            text: fullName(inactiveUser),
            icon: 'minus-circle',
            tooltip: translate.instant('UserManagement.UserAvatar.InactiveUser')
        };
    }
    return null;
}

function fullName(user: User): string {
    return user.firstName + ' ' + user.lastName;
}
